/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
import { OFFER, DISPLAY } from "../../../constants/common";
import GA4GTMDLService from "./gtmDataLayer.service";
import { localStorageUtil } from "../../utils/storageUtil";
import { CURRENT_USER } from "../../constants/common";
//Custom libraries
import v2CategoryResourceService from "../../apis/search/categories.service";
import { LISTER } from "../../constants/gtm";
import { ImageAspectRatioOutlined } from "@material-ui/icons";

let PAGETITLE = "";
let PAGEPATH = "";

const GA4DataService = {
  setPageTitle(title: any) {
    PAGETITLE = title; // eslint-disable-line @typescript-eslint/no-unused-vars
  },
  setPagePath(path: any) {
    PAGEPATH = path; // eslint-disable-line @typescript-eslint/no-unused-vars
  },
  getPagePath() {
    const locationPath = window.location.pathname;
    return locationPath === "/" ? locationPath : locationPath.replace(/\/$/, "");
  },
  getUserInfoFromStore() {
    const currentUser = localStorageUtil.get(CURRENT_USER);
    if (currentUser) {
      const { userId, userLoggedIn } = currentUser;
      return {
        login: userLoggedIn ? "logged In" : "logged Out",
        userID: userId,
      };
    } else {
      return {
        login: "logged Out",
        userID: -1002,
      };
    }
  },
  async sendAddToCartEvent(cart, currentProdSelect, breadcrumbs) {
    const cartObj = {
      grandTotal: cart.grandTotal,
      grandTotalCurrency: cart.grandTotalCurrency,
    };
    //const { id, name, partNumber, price, type } = currentProdSelect.sku;
    const { name, partNumber, price, type, manufacturer } = currentProdSelect.sku[0];
    const productsObj: Array<object> = [];
    const currencyCode = this.getProductPrice(price).currency;
    const catGroups = this.getBreadCrumbsData(breadcrumbs).category.split("/");

    const obj = {
      //id: id,
      id: partNumber,
      name: name,
      type: type,
      partNumber: partNumber,
      price: this.getProductPrice(price).price,
      //category: this.getBreadCrumbsData(breadcrumbs).category,
      variant: currentProdSelect["selectedAttributes"].asgswatchcolor || "",
      quantity: currentProdSelect.quantity,
      brand: manufacturer,
      ...(catGroups[0] && { category: catGroups[0] }),
      ...(catGroups[1] && { category2: catGroups[1] }),
      ...(catGroups[2] && { category3: catGroups[2] }),
      ...(catGroups[3] && { category4: catGroups[3] }),
    };
    productsObj.push(obj);
    return { cartObj, productsObj, currencyCode };
  },

  /**
   * get the product offer price and display price
   * @param priceArray
   */
  getProductPrice(priceArray) {
    let productOfferPrice = 0;
    let prodDisplayPrice = 0;
    let currency: string = "";
    if (priceArray) {
      for (const price of priceArray) {
        if (price.usage === OFFER && price.value !== "") {
          productOfferPrice = parseFloat(price.value);
          currency = price.currency;
        } else if (price.usage === DISPLAY && price.value !== "") {
          prodDisplayPrice = parseFloat(price.value);
          currency = price.currency;
        }
      }
    }
    const price = productOfferPrice > 0 ? productOfferPrice : prodDisplayPrice > 0 ? prodDisplayPrice : 0;
    return { price, currency, prodDisplayPrice };
  },

  getBreadCrumbsData(breadcrumbs) {
    const breadcrumbLabels = breadcrumbs.map((data) => data.label);
    const category = breadcrumbLabels.slice(0, breadcrumbLabels.length - 1).join("/");
    const subCategory = breadcrumbLabels[breadcrumbLabels.length - 1];

    return { breadcrumbLabels, category, subCategory };
  },

  async sendRemoveFromCartEvent(item) {
    const { partNumber, productId, name, orderItemPrice, quantity, currency } = item;
    const product = {
      id: productId,
      name: name,
      partNumber: partNumber,
      price: parseFloat(orderItemPrice),
      quantity: parseInt(quantity),
      currency,
    };
    return product;
  },

  async sendListerPageViewEvent(productListTotal, breadcrumbs) {
    const obj = {
      pageCategory: LISTER,
      pageSubCategory: this.getBreadCrumbsData(breadcrumbs).category,
      listerResults: productListTotal,
      pageTitle: PAGETITLE,
      pagePath: this.getPagePath(),
      ...this.getUserInfoFromStore(),
    };
    return obj;
  },
  sendPurchaseEvent(cart, orderItems) {
    const {
      orderId,
      totalProductPrice,
      totalSalesTax,
      totalShippingTax,
      totalShippingCharge,
      totalAdjustment,
      affiliation,
      brand,
    } = cart;
    const partNumberCatgroupMap = new Map<any, any>();
    let currency = null;
    let itemVariant = "";
    const codeCoupon: Array<object> = [];
    if (cart.promotionCode) {
      cart.promotionCode.map((e) => {
        codeCoupon.push(e.code);
      });
    }
    let productArr = orderItems.map((order) => {
      order.attributes.map((attr) => {
        if (attr["identifier"] === "asgswatchcolor") {
          itemVariant = attr["values"][0].identifier;
        }
      });
      const parentCatalogGroupID = order.parentCatalogGroupID;
      //OOB code fails when processing a order item whose catentry belong to two categories.
      // added code to find the first catgroup .
      const arr =
        Array.isArray(parentCatalogGroupID) && parentCatalogGroupID.length > 0
          ? parentCatalogGroupID[0].split("/")
          : parentCatalogGroupID.split("/");
      currency = order.currency;
      if (arr && arr.length >= 1) {
        let catgroupID = arr[arr.length - 1];
        catgroupID = catgroupID.substring(catgroupID.indexOf("_") + 1);
        partNumberCatgroupMap.set(order.partNumber, catgroupID);
      }
      return {
        id: order.partNumber,
        name: order.name,
        price: order.unitPrice,
        brand: order.brand,
        variant: itemVariant,
        quantity: parseInt(order.quantity),
        discount: order.totalAdjustment.value,
        affiliation: order.affiliation || "Online Store",
        coupon: codeCoupon,
      };
    });

    const set = new Set(Array.from(partNumberCatgroupMap.values()));
    const param = { id: Array.from(set) };

    this.fetchCategoriesId(param)
      .then((value) => {
        const productArrWithCategory = productArr.map((element) => {
          const partNumber = element.id;
          if (partNumberCatgroupMap.get(partNumber)) {
            const categoryId = partNumberCatgroupMap.get(partNumber);
            const categoryName = value.get(categoryId);
            if (categoryName) {
              return {
                category: categoryName || "Search Results",
                id: element.id,
                name: element.name,
                price: element.price,
                quantity: element.quantity,
                variant: element.variant,
                brand: element.brand,
              };
            } else {
              return {
                category: categoryId,
                id: element.id,
                name: element.name,
                price: element.price,
                quantity: element.quantity,
                variant: element.variant,
                brand: element.brand,
              };
            }
          }
          return null;
        });
        productArr = null;
        const obj = {
          purchaseId: orderId,
          totalcost: totalProductPrice,
          salesTax: totalSalesTax,
          shippingTax: totalShippingTax,
          shippingcost: totalShippingCharge,
          totalDiscount: totalAdjustment,
          affiliation: affiliation || "Online Store",
          variant: itemVariant,
          brand: brand,
          currency: currency,
          coupon: codeCoupon,
          orderSubtotal: cart.grandTotal,
          productArrWithCategory,
        };
        GA4GTMDLService.measuringPurchases(obj);
      })
      .catch((error) => {
        console.log("Could not get category ID from search ", error);
      });
  },

  async fetchCategoriesId(param: any) {
    const result = await v2CategoryResourceService.getV2CategoryResourcesUsingGET(param);
    const contentMap = new Map<any, any>();
    const content = result.data.contents;
    if (content) {
      content.forEach((element) => {
        contentMap.set(element.id, element.name);
      });
    }
    return contentMap;
  },

  async sendCheckoutEvent(cart, orderItems, step, value) {
    let paymentMethod = "";
    const cartObj = {
      grandTotal: cart.grandTotal,
      grandTotalCurrency: cart.grandTotalCurrency,
      cartId: cart?.orderId,
    };

    if (cart && cart.paymentInstruction) {
      paymentMethod = cart.paymentInstruction[0].payMethodId;
    }

    const productArr = orderItems.map((order) => {
      let itemVariant = "";
      let itemCategory = "";
      order.attributes.map((attr) => {
        if (attr["identifier"] === "asgswatchcolor") {
          itemVariant = attr["values"][0].identifier;
        }
        if (attr["identifier"] === "ItemType") {
          itemCategory = attr["values"][0].identifier;
        }
      });

      return {
        id: order.partNumber,
        name: order.name,
        price: order.orderItemPrice,
        quantity: parseInt(order.quantity),
        currency: order.currency,
        brand: order.brand,
        //shippingTier: order.shipModeCode,
        shippingTier: order.carrier,
        category: itemCategory,
        variant: itemVariant,
      };
    });

    return { cartObj, step, value, productArr, paymentMethod };
  },

  async sendViewCartEvent(cart, orderItems) {
    const cartObj = {
      grandTotal: cart?.grandTotal,
      grandTotalCurrency: cart?.grandTotalCurrency,
      cartId: cart?.orderId,
    };
    const productArr = orderItems.map((order) => {
      let itemVariant = "";
      let itemCategory = "";
      order.attributes.map((attr) => {
        if (attr["identifier"] === "asgswatchcolor") {
          itemVariant = attr["values"][0].identifier;
        }
        if (attr["identifier"] === "ItemType") {
          itemCategory = attr["values"][0].identifier;
        }
      });

      return {
        id: order.partNumber,
        name: order.name,
        price: order.orderItemPrice,
        quantity: parseInt(order.quantity),
        currency: order.currency,
        //shippingTier: order.shipModeCode,
        shippingTier: order.carrier,
        brand: order.brand,
        //category: order.category,
        //variant: order.variant,
        category: itemCategory,
        variant: itemVariant,
        tax: order.tax,
        discount: order.discount,
        affiliation: order.affiliation || "Online Store",
      };
    });
    return { cartObj, productArr };
  },

  async sendPromotionImpression(promo) {
    if (promo.baseMarketingSpotActivityData) {
      const { contentId, contentName } = promo.baseMarketingSpotActivityData[0];
      const promoArr = [
        {
          id: contentId,
          name: contentName,
        },
      ];
      return promoArr;
    }
  },

  async sendPromotionClick(promo) {
    const { contentId, contentName } = promo.baseMarketingSpotActivityData[0];
    const promoObj = {
      id: contentId,
      name: contentName,
    };
    return promoObj;
  },

  async sendProductImpressionEvent(productList, listerFlag, breadcrumbs) {
    const catGroups = this.getBreadCrumbsData(breadcrumbs).category.split("/");
    const productArr = productList.map((product, index) => {
      return {
        name: product.name,
        id: product.sku,
        brand: product.brand,
        price: product.price /*this.getProductPrice(product.price).price,*/,
        variant: product.variant,
        category: listerFlag ? this.getBreadCrumbsData(breadcrumbs).category : "",
        list: listerFlag ? this.getBreadCrumbsData(breadcrumbs).subCategory : "Search Results",
        position: index + 1,
        currency: this.getProductPrice(product.price).currency,
        quantity: "1",
        ...(catGroups[0] && { category: catGroups[0] }),
        ...(catGroups[1] && { category2: catGroups[1] }),
        ...(catGroups[2] && { category3: catGroups[2] }),
        ...(catGroups[3] && { category4: catGroups[3] }),
      };
    });
    const currency = productArr?.length > 0 ? productArr[0].currency : "";
    return { productArr, currency };
  },

  async sendProductClickEvent(product, index, listerFlag, breadcrumbs) {
    const breadcrumbLength = this.getBreadCrumbsData(breadcrumbs).breadcrumbLabels.length;
    const catGroups = this.getBreadCrumbsData(breadcrumbs).category.split("/");
    const obj = {
      id: product.sku,
      name: product.name,
      brand: product.brand,
      quantity: product.quantity,
      //price: this.getProductPrice(product.price).price,
      price: product.price,
      //category: listerFlag ? this.getBreadCrumbsData(breadcrumbs).category : "",
      list: listerFlag ? this.getBreadCrumbsData(breadcrumbs).subCategory : "Search Results",
      position: index + 1,
      currency: this.getProductPrice(product.price).currency,
      url: window.location.href,
      imageUrl: product.imageUrl,
      ...(catGroups[0] && { category: catGroups[0] }),
      ...(catGroups[1] && { category2: catGroups[1] }),
      ...(catGroups[2] && { category3: catGroups[2] }),
      ...(catGroups[3] && { category4: catGroups[3] }),
    };
    return obj;
  },

  async sendPDPDetailViewEvent(currentProdSelect, breadcrumbs) {
    const breadcrumbLength = this.getBreadCrumbsData(breadcrumbs).breadcrumbLabels.length;
    //const { id, name, price } = currentProdSelect.sku;
    const { partNumber, name, price, manufacturer } = currentProdSelect.sku[currentProdSelect.index];
    const catGroups = this.getBreadCrumbsData(breadcrumbs).category.split("/");
    const sku = partNumber.split(".")[0];
    const obj = {
      //id: id,
      id: partNumber,
      name: name,
      item: sku,
      //price: this.getProductPrice(price).price,
      price: this.getProductPrice(price).prodDisplayPrice,
      //category: this.getBreadCrumbsData(breadcrumbs).category,
      variant: currentProdSelect["selectedAttributes"].asgswatchcolor || "",
      list: this.getBreadCrumbsData(breadcrumbs).breadcrumbLabels[breadcrumbLength - 2],
      quantity: currentProdSelect.quantity,
      currency: this.getProductPrice(price).currency,
      brand: manufacturer,
      imageUrl: currentProdSelect.sku[0].fullImage,
      url: window.location.href,
      salePrice: currentProdSelect.sku[0].price[1].value,
      ...(catGroups[0] && { category: catGroups[0] }),
      ...(catGroups[1] && { category2: catGroups[1] }),
      ...(catGroups[2] && { category3: catGroups[2] }),
      ...(catGroups[3] && { category4: catGroups[3] }),
    };
    return obj;
  },

  async sendB2BAddToCartEvent(cart, currentProdSelect, result, breadcrumbs) {
    const cartObj = {
      grandTotal: cart.grandTotal,
      grandTotalCurrency: cart.grandTotalCurrency,
    };
    const { category, subCategory } = this.getBreadCrumbsData(breadcrumbs);
    const price = currentProdSelect.partNumber.price;
    const productsObj: Array<object> = [];
    const currencyCode = this.getProductPrice(price).currency;
    result.forEach((element) => {
      const obj = {
        id: element.key,
        name: subCategory,
        price: this.getProductPrice(price).price,
        category: category,
        quantity: element.value,
      };
      productsObj.push(obj);
    });
    return { cartObj, productsObj, currencyCode };
  },

  async sendB2BPDPDetailViewEvent(productData, productPartNumber, breadcrumbs) {
    const { breadcrumbLabels, category, subCategory } = this.getBreadCrumbsData(breadcrumbs);
    const leafCategoryForProduct = breadcrumbLabels[breadcrumbLabels.length - 2];
    const obj = {
      id: productPartNumber,
      name: subCategory,
      price: productData && this.getProductPrice(productData.price).price,
      category: category,
      list: leafCategoryForProduct,
      currency: productData ? this.getProductPrice(productData.price).currency : "",
    };
    return obj;
  },
  async sendUserDeatilsPageViewEvent(currentUser) {
    let obj = {};
    //const currentUser = localStorageUtil.get(CURRENT_USER);
    if (currentUser !== null) {
      const email = currentUser.details.email1
        ? currentUser.details.email1
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].email1
        : undefined;
      const city = currentUser.details.city
        ? currentUser.details.city
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].city
        : undefined;
      const country = currentUser.details.country
        ? currentUser.details.country
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].country
        : undefined;
      const zipCode = currentUser.details.zipCode
        ? currentUser.details.zipCode
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].zipCode
        : undefined;
      const state = currentUser.details.state
        ? currentUser.details.state
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].state
        : undefined;
      const firstName = currentUser.details.firstName
        ? currentUser.details.firstName
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].firstName
        : undefined;
      const lastName = currentUser.details.lastName
        ? currentUser.details.lastName
        : currentUser.details?.contact
        ? currentUser.details?.contact[0].lastName
        : undefined;
      obj = {
        user_id: currentUser.details.userId,
        user_type: currentUser.isGuest ? "Guest" : "Registered",
        user_email: currentUser.details.logonId,
        ...(city && { user_city: city }),
        ...(country && { user_country: country }),
        ...(zipCode && { user_postalCode: zipCode }),
        ...(state && { user_state: state }),
        ...(firstName && { user_firstName: firstName }),
        ...(lastName && { user_lastName: lastName }),
      };
    }
    return obj;
  },
};

export default GA4DataService;
